// Közös megerősítő dialógus — törlés / veszélyes / figyelmeztető művelet előtt. i18n-vezérelt szövegek,
// „folyamatban" állapot (a megerősítő gomb disabled), cancel. A szülő a `open`/`onOpenChange`-dzsel vezérli.
// TÍPUSOS (`tone`): `danger` = destruktív (piros ikon + piros gomb, pl. törlés), `warning` = figyelmeztető
// (sárgás ikon + sárga gomb), `default` = semleges (nincs ikon, alap gomb). A fejléc-ikon a súlyosság vizuális jele.
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export type ConfirmTone = 'default' | 'warning' | 'danger';

// A súlyosság-jel a TARTALOMBAN (alert-szerű), kis színes ikonnal — NEM a headerben (nem tolja beljebb a címet).
const TONE_ICON_COLOR: Record<'warning' | 'danger', string> = {
  warning: 'text-warning',
  danger: 'text-danger-600',
};

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** A megerősítő gomb felirata (pl. „Törlés"). */
  confirmLabel: string;
  cancelLabel: string;
  /** A folyamatban lévő művelet feliratja (pl. „Törlés…"), amíg pending. */
  pendingLabel?: string;
  /** A modal SÚLYOSSÁGA: színezett fejléc-ikon + megerősítő gomb. */
  tone?: ConfirmTone;
  /** @deprecated Használd a `tone="danger"`-t. Visszafelé kompat: `true` → `tone="danger"`. */
  destructive?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  /**
   * Opcionális RÉSZLET a leírás alatt (pl. egy tétel-lista). Külön slot, mert a `DialogDescription` egy `<p>`:
   * blokk-elem (lista) bele érvénytelen DOM lenne.
   */
  children?: ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  pendingLabel,
  tone,
  destructive,
  pending = false,
  onConfirm,
  children,
}: ConfirmDialogProps): JSX.Element {
  const effTone: ConfirmTone = tone ?? (destructive ? 'danger' : 'default');
  const confirmVariant =
    effTone === 'danger' ? 'destructive' : effTone === 'warning' ? 'warning' : 'brand';
  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {(Boolean(description) || Boolean(children)) && (
          <DialogBody>
            {!description ? null : effTone === 'default' ? (
              <DialogDescription>{description}</DialogDescription>
            ) : (
              // Súlyosság-jel a tartalomban: kis színes figyelmeztető ikon a szöveg mellett (alert-minta).
              <div className="flex gap-2.5">
                <ExclamationTriangleIcon
                  className={cn('mt-0.5 size-5 shrink-0', TONE_ICON_COLOR[effTone])}
                  aria-hidden="true"
                />
                <DialogDescription>{description}</DialogDescription>
              </div>
            )}
            {children}
          </DialogBody>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {cancelLabel}
          </Button>
          <Button type="button" variant={confirmVariant} onClick={onConfirm} disabled={pending}>
            {pending ? (pendingLabel ?? confirmLabel) : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
